/**
 * Range zone calculation utilities for chart shading.
 *
 * Mirrors the backend's zone categorization from diagnostic_knots.py:
 *   get_marker_direction() → "high" | "elevated" | "normal" | "depressed" | "low"
 *
 * Zone mapping:
 *   "high"      → abnormalHigh   (above lab_high)
 *   "elevated"  → dysfunctionalHigh (above fx_high, within lab)
 *   "normal"    → optimal        (within fx range)
 *   "depressed" → dysfunctionalLow  (below fx_low, within lab)
 *   "low"       → abnormalLow    (below lab_low)
 *
 * One-sided ranges are supported: if only fxLow is set (e.g. eGFR >= 59),
 * optimal extends from fxLow to domainMax. Same for lab boundaries.
 */

/**
 * Safely parse a range value to a finite number or null.
 * Guards against undefined, null, NaN, empty strings, and Infinity.
 */
export function parseRangeValue(value) {
    if (value == null || value === '') return null;
    const num = Number(value);
    return Number.isFinite(num) ? num : null;
}

/**
 * Parse a result value that may have inequality prefixes (e.g. "<9", ">100").
 * Strips leading < or > and returns the numeric value, or null if unparseable.
 */
export function parseResultValue(value) {
    if (value == null) return null;
    const str = String(value).trim();
    const stripped = str.replace(/^[<>]\s*/, '');
    if (stripped === '') return null;
    const num = Number(stripped);
    return Number.isFinite(num) ? num : null;
}

/**
 * Compute range zone boundaries for chart shading.
 *
 * @param {Object} params
 * @param {number|null} params.fxLow  - Functional range low boundary
 * @param {number|null} params.fxHigh - Functional range high boundary
 * @param {number|null} params.labLow  - Lab range low boundary
 * @param {number|null} params.labHigh - Lab range high boundary
 * @param {number} params.domainMin - Chart y-axis minimum (from data + padding)
 * @param {number} params.domainMax - Chart y-axis maximum (from data + padding)
 * @returns {Object} Zone objects with { y1, y2 } or null if zone doesn't apply
 */
export function computeRangeZones({ fxLow: rawFxLow, fxHigh: rawFxHigh, labLow, labHigh, domainMin, domainMax }) {
    let fxLow = rawFxLow;
    let fxHigh = rawFxHigh;
    if (labLow !== null && labHigh !== null) {
        if ((fxHigh !== null && fxHigh < labLow) || (fxLow !== null && fxLow > labHigh)) {
            fxLow = null;
            fxHigh = null;
        }
    }
    if (labHigh > 0 && fxHigh !== null && fxHigh > 0) {
        const ratio = labHigh / fxHigh;
        if (ratio > 50 || ratio < 1 / 50) {
            fxLow = null;
            fxHigh = null;
        }
    }

    const hasFxRange = fxLow !== null || fxHigh !== null;
    const hasLabRangeInput = labLow !== null || labHigh !== null;

    // If no lab range but FX exists, use FX values as lab boundaries
    const effectiveLabLow = hasLabRangeInput ? labLow : (hasFxRange ? fxLow : null);
    const effectiveLabHigh = hasLabRangeInput ? labHigh : (hasFxRange ? fxHigh : null);
    const hasLabRange = effectiveLabLow !== null || effectiveLabHigh !== null;

    // If no FX range specified, fall back to lab range for optimal zone
    const usedFxLow = hasFxRange ? fxLow : effectiveLabLow;
    const usedFxHigh = hasFxRange ? fxHigh : effectiveLabHigh;
    const hasOptimalRange = usedFxLow !== null || usedFxHigh !== null;

    // Clamp FX within lab boundaries — matches backend where lab is the outer boundary
    const clampedFxLow = (usedFxLow !== null && effectiveLabLow !== null && usedFxLow < effectiveLabLow) ? effectiveLabLow : usedFxLow;
    const clampedFxHigh = (usedFxHigh !== null && effectiveLabHigh !== null && usedFxHigh > effectiveLabHigh) ? effectiveLabHigh : usedFxHigh;

    // One-sided support: missing boundary extends to domain edge
    const optimalLow = clampedFxLow ?? domainMin;
    const optimalHigh = clampedFxHigh ?? domainMax;
    const labLowVal = effectiveLabLow ?? domainMin;
    const labHighVal = effectiveLabHigh ?? domainMax;

    // Clamp zone to visible chart domain; returns null if zone has no visible portion.
    // When boundaries are equal (point range), expand to a thin visible band.
    const clampZone = (y1, y2) => {
        const cy1 = Math.max(y1, domainMin);
        const cy2 = Math.min(y2, domainMax);
        if (cy1 > cy2) return null;
        if (cy1 === cy2) {
            const nudge = (domainMax - domainMin) * 0.01 || 0.01;
            return { y1: Math.max(cy1 - nudge, domainMin), y2: Math.min(cy2 + nudge, domainMax) };
        }
        return { y1: cy1, y2: cy2 };
    };

    return {
        // Green: within FX optimal range (or lab range if no FX)
        optimal: hasOptimalRange ? clampZone(optimalLow, optimalHigh) : null,

        // Yellow: between lab boundary and FX boundary (dysfunctional)
        // Only when FX range is explicitly provided (not lab-fallback)
        // One-sided lab ranges (e.g. <200): null bound → domainMin/Max via labLowVal/labHighVal
        dysfunctionalLow: (hasFxRange && hasLabRange && clampedFxLow !== null && optimalLow > labLowVal)
            ? clampZone(labLowVal, optimalLow) : null,
        dysfunctionalHigh: (hasFxRange && hasLabRange && clampedFxHigh !== null && labHighVal > optimalHigh)
            ? clampZone(optimalHigh, labHighVal) : null,

        // Red: outside lab range (abnormal)
        abnormalLow: (hasLabRange && labLowVal > domainMin)
            ? clampZone(domainMin, labLowVal) : null,
        abnormalHigh: (hasLabRange && labHighVal < domainMax)
            ? clampZone(labHighVal, domainMax) : null,
    };
}
